import React, { useEffect, useRef, useState } from 'react';
import { MONTHS, compactRound as cr, moneyFull, intFull, change, pageLabel, TextField, NumberField } from './bulletinShared';

// Aylık bülten — "sunum" şablonu (2 slayt, 16:9).
// Tasarım Yataş Bedding'in hazırladığı "YatasBedding_Agustos2026_Rapor.pptx" taslağının
// birebir karşılığı: slayt 1 = ayın performansı, slayt 2 = gelecek ayın planı.
// Slaytlar 1280×720 tuvalde çizilir ve kap genişliğine ölçeklenir (PPTX koordinatları
// doğrudan kullanılabilsin diye).

const C = {
  navy: '#0D1B3E', blue: '#2E5CB8', teal: '#0A7A6E', tealDark: '#09564D', green: '#1A6B3C',
  red: '#B8280A', gold: '#B8860B', grey: '#6B7A99', line: '#D8E2F0', bg: '#F7F9FD',
  blueBg: '#E8EFFC', blueLine: '#B8CEEF', tealBg: '#E0F5F2', tealLine: '#A0DDD6',
  goldBg: '#FEF8E6', goldLine: '#E8D88A', headSub: '#A0B4D8', okGreen: '#5EB88A',
};
const LOGO = 'https://api.glowytics.com/pixel/yatas-bedding-logo-beyaz.png';

const money = (v) => (v == null || !isFinite(v) ? '–' : `₺${cr(v)}`);
const dur = (s) => (s == null || !isFinite(s) ? '–' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`);
const pct1 = (v) => (v == null || !isFinite(v) ? '–' : `%${v.toFixed(1).replace('.', ',')}`);

// ▲ %13 (Tem.) — iyi/kötü rengi metriğin yönüne göre (hemen çıkma için düşüş iyidir)
function Delta({ ch, short, lowerIsBetter, size = 11 }) {
  if (ch == null || !isFinite(ch)) return <div style={{ height: size + 4 }} />;
  const up = ch >= 0;
  const good = lowerIsBetter ? !up : up;
  return (
    <div style={{ fontSize: size, fontWeight: 700, color: good ? C.green : C.red, whiteSpace: 'nowrap' }}>
      {up ? '▲' : '▼'} %{Math.abs(Math.round(ch))} ({short})
    </div>
  );
}

function Scaled({ children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, e.contentRect.width / 1280)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="bt-slide-wrap" style={{ width: '100%', height: 720 * scale, position: 'relative' }}>
      <div className="bt-slide" style={{ width: 1280, height: 720, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', left: 0, top: 0 }}>
        {children}
      </div>
    </div>
  );
}

function Header({ color, title, right, sub }) {
  return (
    <div style={{ height: 69, background: color, display: 'flex', alignItems: 'center', padding: '0 33px 0 30px' }}>
      <img src={LOGO} alt="Yataş Bedding" style={{ height: 34, width: 200, objectFit: 'contain', objectPosition: 'left' }} />
      <div style={{ marginLeft: 39, color: '#fff', fontWeight: 700, fontSize: 14.5 }}>{title}</div>
      <div style={{ marginLeft: 'auto', color: sub, fontSize: 12.5 }}>{right}</div>
    </div>
  );
}

function Footer({ text, page }) {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 31, background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 33px 0 37px', fontSize: 10.5, color: '#4D6A99' }}>
      <span>{text}</span><span>{page}</span>
    </div>
  );
}

function SectionLabel({ children, color = C.navy, top }) {
  return (
    <div style={{ position: 'absolute', left: 19, right: 23, top }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{ width: 7, height: 7, background: color, display: 'inline-block', marginTop: 6 }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 2, color: C.grey, textTransform: 'uppercase' }}>{children}</span>
      </div>
      <div style={{ height: 1.5, background: C.navy, marginTop: 4 }} />
    </div>
  );
}

function Kpi({ label, value, sub, delta, bar }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', height: 92, position: 'relative' }}>
      <div style={{ height: 4, background: bar }} />
      <div style={{ padding: '4px 10px 0 12px' }}>
        <div style={{ fontSize: 10, color: C.grey, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
        <div style={{ fontSize: 25, fontWeight: 700, color: C.navy, lineHeight: '32px', whiteSpace: 'nowrap' }}>{value}</div>
        {delta}
        <div style={{ fontSize: 9.5, color: C.grey, whiteSpace: 'nowrap' }}>{sub}</div>
      </div>
    </div>
  );
}

function Metric({ label, value, delta, valueColor = C.navy, big }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.grey, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: big ? 24 : 22, fontWeight: 700, color: valueColor, lineHeight: '30px', whiteSpace: 'nowrap' }}>{value}</div>
      {delta}
    </div>
  );
}

const panel = 'bt-noprint mt-3 mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 grid gap-3 md:grid-cols-2 text-sm';
const lbl = 'block text-xs font-semibold text-amber-900 mb-1';

export default function BulletinDeck({ data, manual, set, editing, year, month }) {
  const a = data.auto;
  const cur = a.current;
  const prev = a.previous;
  const ov = manual.overrides || {};
  const pick = (k, src = cur) => (ov[k] != null ? ov[k] : src[k]);

  const monthName = MONTHS[month - 1];
  const prevShort = `${MONTHS[(month + 10) % 12].slice(0, 3)}.`;
  const nextName = MONTHS[month % 12];
  const nextYear = month === 12 ? year + 1 : year;

  const v = {
    cost: pick('cost'), sessions: pick('sessions'), users: pick('users'), impressions: pick('impressions'),
    views: pick('views'), revenue: pick('revenue'), bounce: pick('bounceRate'),
    duration: ov.avgSessionDuration ?? a.avgSessionDuration,
    directions: ov.directions ?? a.directions?.current,
    costPerformance: pick('costPerformance'), costBranding: pick('costBranding'),
    sessionsPerformance: pick('sessionsPerformance'), sessionsBranding: pick('sessionsBranding'),
    revenuePerformance: pick('revenuePerformance'), revenueBranding: pick('revenueBranding'),
    impressionsBranding: pick('impressionsBranding'), viewsBranding: pick('viewsBranding'),
  };

  const kpis = [
    { k: 'cost', label: 'Reklam Harcaması', value: money(v.cost), sub: 'Branding + Performans', ch: change(v.cost, prev.cost), bar: C.red },
    { k: 'sessions', label: 'Web Trafiği', value: cr(v.sessions), sub: 'Ziyaret Sayısı', ch: change(v.sessions, prev.sessions), bar: C.blue },
    { k: 'users', label: 'Tekil Kullanıcı', value: cr(v.users), sub: 'Tekil Ziyaretçi', ch: change(v.users, prev.users), bar: C.blue },
    { k: 'impressions', label: 'Reklam Gösterimi', value: cr(v.impressions), sub: 'Gösterim', ch: change(v.impressions, prev.impressions), bar: C.navy },
    { k: 'views', label: 'Video İzlenme', value: cr(v.views), sub: 'İzlenme', ch: change(v.views, prev.views), bar: C.teal },
    { k: 'directions', label: 'Yol Tarifi Alma', value: v.directions != null ? intFull(v.directions) : '–', sub: 'Google Business Profile', ch: change(v.directions, a.directions?.previous), bar: C.teal },
    { k: 'avgSessionDuration', label: 'Sitede Kalma Süresi', value: dur(v.duration), sub: 'Dk:sn', ch: change(v.duration, a.avgSessionDurationPrev), bar: C.green },
    { k: 'bounceRate', label: 'Hemen Çıkma Oranı', value: pct1(v.bounce), sub: 'Etkileşimsiz oturum', ch: change(v.bounce, prev.bounceRate), bar: C.green, lower: true },
    { k: 'revenue', label: 'Toplam Gelir', value: money(v.revenue), sub: 'KDV Hariç Ciro', ch: change(v.revenue, prev.revenue), bar: C.green },
  ];

  // Ayın kampanyası şeridi
  const camp = manual.campaign || {};
  const cs = camp.stats || {};
  const keyword = (camp.videoKeyword || '').trim();
  const campImpr = cs.impressions ?? (keyword ? a.video?.impressions : v.impressions);
  const campViews = cs.views ?? (keyword ? a.video?.views : v.views);
  const campRate = cs.completion ?? a.video?.completionRate;
  const tagline = camp.tagline || `${monthName.toUpperCase()} KAMPANYASI`;

  // Öne çıkanlar
  const hl = manual.highlights || {};
  const channel = (a.channels || []).find((c) => c.label === hl.channel) || (a.channels || [])[0];
  const productOpts = a.products || [];
  const products = hl.products && hl.products.length ? hl.products : productOpts.slice(0, 6).map((p) => p.name);
  const pageOpts = a.pages || [];
  const pages = hl.pages && hl.pages.length ? hl.pages : pageOpts.slice(0, 3).map((p) => ({ path: p.path, label: pageLabel(p) }));

  // Slayt 2
  const nx = manual.next || {};
  const nt = a.nextTargets || {};
  const tgt = (k) => (nx.targets?.[k] != null ? nx.targets[k] : nt[k]);
  const budgetTotal = (tgt('costPerformance') || 0) + (tgt('costBranding') || 0);
  const campaigns = String(nx.campaigns || '').split('\n').map((x) => x.trim()).filter(Boolean);
  const chips = String(nx.channels || (a.activeChannels || []).join(', ')).split(',').map((x) => x.trim()).filter(Boolean);
  const goal = (curV, tV) => {
    if (curV == null || tV == null || !curV) return '';
    const p = ((tV - curV) / curV) * 100;
    return `Hedef ${p >= 0 ? '▲' : '▼'} %${Math.abs(Math.round(p))}`;
  };
  const strip = [
    { label: 'Ciro', a: money(v.revenue), b: money(tgt('revenue')), note: goal(v.revenue, tgt('revenue')), bar: C.teal },
    { label: 'Trafik', a: cr(v.sessions), b: cr(tgt('sessions')), note: goal(v.sessions, tgt('sessions')), bar: C.blue },
    { label: 'Gösterim', a: cr(v.impressions), b: cr(tgt('impressions')), note: goal(v.impressions, tgt('impressions')), bar: C.navy },
    { label: 'İzlenme', a: cr(v.views), b: cr(tgt('views')), note: goal(v.views, tgt('views')), bar: C.teal },
    { label: 'Toplam Bütçe', a: money(v.cost), b: budgetTotal ? money(budgetTotal) : '–', note: goal(v.cost, budgetTotal || null), bar: C.gold },
  ];
  const footer = `Yataş Bedding Dijital Pazarlama  ·  ${monthName} ${year}`;

  return (
    <div className="space-y-6">
      {/* ── SLAYT 1 ── */}
      <div className="bt-section">
        <Scaled>
          <div style={{ width: 1280, height: 720, background: C.bg, position: 'relative', overflow: 'hidden', fontFamily: "Calibri, 'Carlito', 'Open Sans', sans-serif" }}>
            <Header color={C.navy} title={`Dijital Performans Raporu  ·  ${monthName} ${year}`} right={`${monthName} ${year}`} sub={C.headSub} />
            <div style={{ position: 'absolute', left: 0, top: 69, width: 4, height: 620, background: C.blue }} />

            <SectionLabel top={80}>Genel Performans</SectionLabel>
            <div style={{ position: 'absolute', left: 19, right: 23, top: 109, display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 9 }}>
              {kpis.map((k) => (
                <Kpi key={k.k} label={k.label} value={k.value} sub={k.sub} bar={k.bar}
                  delta={<Delta ch={k.ch} short={prevShort} lowerIsBetter={k.lower} />} />
              ))}
            </div>

            <SectionLabel top={214} color={C.blue}>Harcama Dağılımları</SectionLabel>
            <div style={{ position: 'absolute', left: 19, right: 23, top: 242, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: C.blueBg, border: `1px solid ${C.blueLine}`, borderRadius: 10, height: 96, padding: '9px 14px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, color: C.blue }}>SATIŞ REKLAMLARI (Performans)</div>
                <div style={{ height: 1, background: C.blueLine, margin: '5px 0 4px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.9fr', gap: 8 }}>
                  <Metric label="Harcama" value={money(v.costPerformance)} delta={<Delta ch={change(v.costPerformance, prev.costPerformance)} short={prevShort} />} />
                  <Metric label="Performans / Trafik" value={cr(v.sessionsPerformance)} delta={<Delta ch={change(v.sessionsPerformance, prev.sessionsPerformance)} short={prevShort} />} />
                  <Metric label="Performans / Ciro" value={money(v.revenuePerformance)} delta={<Delta ch={change(v.revenuePerformance, prev.revenuePerformance)} short={prevShort} />} />
                  {(() => {
                    // PPTX'teki "Büyüme": performans cirosunun önceki aya göre değişimi
                    const g = change(v.revenuePerformance, prev.revenuePerformance);
                    return <Metric label="Büyüme" big valueColor={g == null || g >= 0 ? C.green : C.red}
                      value={g == null ? '–' : `${g >= 0 ? '▲' : '▼'} %${Math.abs(Math.round(g))}`} />;
                  })()}
                </div>
              </div>
              <div style={{ background: C.tealBg, border: `1px solid ${C.tealLine}`, borderRadius: 10, height: 96, padding: '9px 14px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, color: C.teal }}>TANITIM REKLAMLARI (Branding)</div>
                <div style={{ height: 1, background: C.tealLine, margin: '5px 0 4px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                  <Metric label="Harcama" value={money(v.costBranding)} delta={<Delta ch={change(v.costBranding, prev.costBranding)} short={prevShort} />} />
                  <Metric label="Branding / Trafik" value={cr(v.sessionsBranding)} delta={<Delta ch={change(v.sessionsBranding, prev.sessionsBranding)} short={prevShort} />} />
                  <Metric label="Branding / Ciro" value={money(v.revenueBranding)} delta={<Delta ch={change(v.revenueBranding, prev.revenueBranding)} short={prevShort} />} />
                  <Metric label="Gösterim" value={cr(v.impressionsBranding)} delta={<Delta ch={change(v.impressionsBranding, prev.impressionsBranding)} short={prevShort} />} />
                  <Metric label="İzlenme" value={cr(v.viewsBranding)} delta={<Delta ch={change(v.viewsBranding, prev.viewsBranding)} short={prevShort} />} />
                </div>
              </div>
            </div>

            <SectionLabel top={352} color={C.teal}>Aya Özel Kampanya</SectionLabel>
            <div style={{ position: 'absolute', left: 19, right: 23, top: 380, height: 84, background: C.navy, borderRadius: 10, display: 'flex', alignItems: 'center', padding: '0 30px 0 31px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 2, color: C.headSub, textTransform: 'uppercase' }}>{tagline}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {camp.title || <span style={{ opacity: 0.5 }}>Kampanya adı girilmedi</span>}
                </div>
              </div>
              {[
                [cr(campImpr), 'Gösterim', '#fff'],
                [cr(campViews), 'İzlenme', '#fff'],
                [campRate != null ? `%${Math.round(campRate)}` : '–', 'İzlenme Oranı', C.okGreen],
              ].map(([val, l, col]) => (
                <div key={l} style={{ width: 118, textAlign: 'center' }}>
                  <div style={{ fontSize: 25, fontWeight: 700, color: col, lineHeight: '30px' }}>{val}</div>
                  <div style={{ fontSize: 9.5, color: C.headSub, marginTop: 4 }}>{l}</div>
                </div>
              ))}
            </div>

            <SectionLabel top={482} color={C.gold}>Ayın Öne Çıkanları</SectionLabel>
            <div style={{ position: 'absolute', left: 19, right: 19, top: 505, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
              {[
                ['En Çok Trafik — Kanal', <><div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginTop: 8 }}>{channel?.label || '–'}</div><div style={{ fontSize: 9.5, color: C.grey, marginTop: 10 }}>{channel ? `${intFull(channel.sessions)} ziyaret` : ''}</div></>],
                ['En Çok Satan Ürünler', <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, marginTop: 6, lineHeight: 1.25 }}>{products.join(' · ')}</div>],
                ['En Yüksek Trafikli Sayfalar', <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, marginTop: 6, lineHeight: 1.25 }}>{pages.map((p) => p.label).join(' · ') || '–'}</div>],
              ].map(([l, body]) => (
                <div key={l} style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, height: 78, padding: '6px 20px' }}>
                  <div style={{ fontSize: 9.5, color: C.grey }}>{l}</div>
                  {body}
                </div>
              ))}
            </div>

            <Footer text={footer} page="01 / 02" />
          </div>
        </Scaled>

        {editing && (
          <div className={panel}>
            <div className="md:col-span-2 font-semibold text-amber-900">Slayt 1 — elle girilen alanlar</div>
            <div>
              <label className={lbl}>Kampanya üst satırı</label>
              <TextField editing value={camp.tagline} onChange={(x) => set('campaign.tagline', x)} placeholder="50. YILA ÖZEL · YOUTUBE · PROGRAMATİK · 1–31 AĞUSTOS" />
            </div>
            <div>
              <label className={lbl}>Kampanya adı</label>
              <TextField editing value={camp.title} onChange={(x) => set('campaign.title', x)} placeholder="Hediye Yağmuru & Serinletici Fırsatlar Cool Wool" />
            </div>
            <div>
              <label className={lbl}>Kampanya sayıları yalnız bu kampanyadan gelsin (adında geçen kelime)</label>
              <TextField editing value={camp.videoKeyword} onChange={(x) => set('campaign.videoKeyword', x)} placeholder="Boşsa ayın toplam gösterim/izlenmesi · kaydettikten sonra güncellenir" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[['impressions', 'Gösterim'], ['views', 'İzlenme'], ['completion', 'İzlenme oranı %']].map(([k, l]) => (
                <div key={k}><label className={lbl}>{l}</label>
                  <NumberField editing value={cs[k]} onChange={(x) => set(`campaign.stats.${k}`, x)} placeholder="oto" /></div>
              ))}
            </div>
            <div>
              <label className={lbl}>En çok trafik getiren kanal</label>
              <select className="w-full text-sm border border-amber-300 rounded px-2 py-1.5 bg-white" value={channel?.label || ''} onChange={(e) => set('highlights.channel', e.target.value)}>
                {(a.channels || []).map((c) => <option key={c.label} value={c.label}>{c.label} — {intFull(c.sessions)}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>En çok satan ürünler (en fazla 6, sırayla)</label>
              <div className="grid grid-cols-2 gap-1.5">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <select key={i} className="text-xs border border-amber-300 rounded px-1 py-1 bg-white" value={products[i] || ''}
                    onChange={(e) => { const arr = [...products]; arr[i] = e.target.value; set('highlights.products', arr.filter(Boolean)); }}>
                    <option value="">—</option>
                    {productOpts.map((p) => <option key={p.name} value={p.name}>{p.name} ({p.qty})</option>)}
                  </select>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className={lbl}>En yüksek trafikli sayfalar</label>
              <div className="grid md:grid-cols-3 gap-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex gap-1">
                    <select className="w-1/2 text-xs border border-amber-300 rounded px-1 py-1 bg-white" value={pages[i]?.path || ''}
                      onChange={(e) => {
                        const po = pageOpts.find((x) => x.path === e.target.value);
                        const arr = pages.map((x) => ({ ...x })); arr[i] = { path: po.path, label: pageLabel(po) };
                        set('highlights.pages', arr);
                      }}>
                      {pageOpts.map((p) => <option key={p.path} value={p.path}>{p.path} — {intFull(p.views)}</option>)}
                    </select>
                    <input className="w-1/2 text-xs border border-amber-300 rounded px-1 py-1" value={pages[i]?.label || ''}
                      onChange={(e) => { const arr = pages.map((x) => ({ ...x })); arr[i] = { ...arr[i], label: e.target.value }; set('highlights.pages', arr); }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className={lbl}>Sayıyı elle düzelt (boşsa otomatik)</label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {[...kpis.map((k) => [k.k, k.label]),
                  ['costPerformance', 'Performans harcama'], ['costBranding', 'Branding harcama'],
                  ['revenuePerformance', 'Performans ciro'], ['revenueBranding', 'Branding ciro']].map(([k, l]) => (
                  <div key={k}><label className="block text-[11px] text-amber-900 mb-0.5">{l}</label>
                    <NumberField editing value={ov[k]} onChange={(x) => set(`overrides.${k}`, x)} placeholder="oto" /></div>
                ))}
              </div>
              {a.directions?.partial && <div className="text-xs text-amber-800 mt-2">Yol tarifi verisi bu ay için henüz tam değil; arka planda tazeleniyor.</div>}
            </div>
          </div>
        )}
      </div>

      {/* ── SLAYT 2 ── */}
      <div className="bt-section">
        <Scaled>
          <div style={{ width: 1280, height: 720, background: C.bg, position: 'relative', overflow: 'hidden', fontFamily: "Calibri, 'Carlito', 'Open Sans', sans-serif" }}>
            <Header color={C.tealDark} title={`${nextName} ${nextYear} Planlaması`} right={`${nextName} ${nextYear}`} sub="#A0C4BE" />

            <SectionLabel top={80} color={C.teal}>{`${nextName} Hedefleri & Bütçe`}</SectionLabel>
            <div style={{ position: 'absolute', left: 19, right: 23, top: 109, height: 364, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div style={{ background: C.blueBg, border: `1px solid ${C.blueLine}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 2, color: C.blue, padding: '0 10px' }}>HEDEFLER</div>
                <div style={{ height: 4, background: C.blueLine, margin: '8px 0 0' }} />
                {[['revenue', 'Hedef Ciro', money], ['sessions', 'Hedef Trafik', cr], ['impressions', 'Hedef Gösterim', cr], ['views', 'Hedef İzlenme', cr]].map(([k, l, f], i) => (
                  <div key={k} style={{ padding: '8px 10px 7px', borderTop: i ? `2px solid ${C.blueLine}` : 'none' }}>
                    <div style={{ fontSize: 11, color: C.grey }}>{l}</div>
                    <div style={{ fontSize: 23, color: C.navy, marginTop: 2 }}>{tgt(k) != null ? f(tgt(k)) : '–'}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: C.tealBg, border: `1px solid ${C.tealLine}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 2, color: C.teal, padding: '0 10px' }}>BÜTÇE DAĞILIMI</div>
                <div style={{ height: 4, background: C.tealLine, margin: '8px 0 0' }} />
                {[['Performans', tgt('costPerformance'), false], ['Branding', tgt('costBranding'), false], ['Toplam', budgetTotal || null, true]].map(([l, val, bold], i) => (
                  <div key={l} style={{ padding: '8px 10px 7px', borderTop: i ? `2px solid ${C.tealLine}` : 'none' }}>
                    <div style={{ fontSize: 11, color: C.grey }}>{l}</div>
                    <div style={{ fontSize: 18.5, color: C.navy, fontWeight: bold ? 700 : 400, marginTop: 4 }}>{val != null ? moneyFull(val) : '–'}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: C.goldBg, border: `1px solid ${C.goldLine}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 2, color: C.gold, padding: '0 10px' }}>KAMPANYALAR & MECRALAR</div>
                <div style={{ height: 4, background: C.goldLine, margin: '8px 0 0' }} />
                <div style={{ padding: '8px 10px 0' }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1, color: C.grey }}>ANA KAMPANYALAR</div>
                  {campaigns.length
                    ? campaigns.slice(0, 6).map((c, i) => (
                      <div key={i} style={{ fontSize: 13, color: C.navy, fontWeight: i === 0 ? 700 : 400, marginTop: i === 0 ? 10 : 12 }}>{c}</div>
                    ))
                    : <div style={{ fontSize: 12, color: C.grey, marginTop: 10 }}>Kampanyalar henüz girilmedi.</div>}
                </div>
                <div style={{ height: 2, background: C.goldLine, margin: '14px 0 8px' }} />
                <div style={{ padding: '0 10px' }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1, color: C.grey, marginBottom: 6 }}>AKTİF MECRALAR</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginLeft: -8, marginRight: -8 }}>
                    {chips.slice(0, 12).map((ch) => (
                      <div key={ch} style={{ background: C.blueBg, border: `1px solid ${C.blueLine}`, borderRadius: 8, height: 23, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: C.blue, whiteSpace: 'nowrap', overflow: 'hidden' }}>{ch}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <SectionLabel top={490}>{`${monthName} Gerçekleşen → ${nextName} Hedef`}</SectionLabel>
            <div style={{ position: 'absolute', left: 19, right: 23, top: 515, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 5 }}>
              {strip.map((s) => (
                <div key={s.label} style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', height: 95 }}>
                  <div style={{ height: 2, background: s.bar }} />
                  <div style={{ padding: '6px 20px' }}>
                    <div style={{ fontSize: 9.5, color: C.grey }}>{s.label}</div>
                    <div style={{ fontSize: 21, fontWeight: 700, color: C.navy, marginTop: 8, whiteSpace: 'nowrap' }}>{s.a} → {s.b}</div>
                    <div style={{ fontSize: 9.5, color: C.grey, marginTop: 14 }}>{s.note}</div>
                  </div>
                </div>
              ))}
            </div>

            <Footer text={footer} page="02 / 02" />
          </div>
        </Scaled>

        {editing && (
          <div className={panel}>
            <div className="md:col-span-2 font-semibold text-amber-900">Slayt 2 — {nextName} planı</div>
            <div>
              <label className={lbl}>Ana kampanyalar (her satıra bir kampanya, ilki kalın yazılır)</label>
              <TextField editing multiline rows={5} value={nx.campaigns} onChange={(x) => set('next.campaigns', x)}
                placeholder={'50. Yıl Özel Uyku Seti Hediye\nSerinleten Fırsatlar Black Ice\nBoyun Yastığı Hediye'} />
            </div>
            <div>
              <label className={lbl}>Aktif mecralar (virgülle; boşsa bu ay harcama yapılanlar)</label>
              <TextField editing value={nx.channels} onChange={(x) => set('next.channels', x)} placeholder="Google Ads, Meta Ads, Pinterest, Bing, RTB, Criteo, Yango, Programatik" />
              <div className="text-xs text-amber-800 mt-2">Hedef ve bütçeler “Target vs Actual” sayfasındaki {nextName} hedeflerinden gelir. Burada yazılan yalnız bu bültende geçerli olur.</div>
            </div>
            <div className="md:col-span-2 grid grid-cols-3 md:grid-cols-6 gap-2">
              {[['revenue', 'Hedef ciro'], ['sessions', 'Hedef trafik'], ['impressions', 'Hedef gösterim'], ['views', 'Hedef izlenme'], ['costPerformance', 'Performans bütçe'], ['costBranding', 'Branding bütçe']].map(([k, l]) => (
                <div key={k}><label className="block text-[11px] text-amber-900 mb-0.5">{l}</label>
                  <NumberField editing value={nx.targets?.[k]} onChange={(x) => set(`next.targets.${k}`, x)} placeholder={nt[k] != null ? `oto ${Math.round(nt[k])}` : 'girilmemiş'} /></div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
